import { buildPrompt } from '@/ai/utils/promptBuilder'
import { getLanguageFromPath } from '@/ai/utils/helpers'
import { systemMessages } from '@/ai/prompts/common/systemMessages'
import { exampleFileTagsOutput } from '@/ai/prompts/common/examples'
import { getJsonOutputFormatting } from '@/ai/prompts/common/formatting'
import { createUserMessage, createSystemMessage } from '@/ai/utils/promptUtils'

export const buildFileTaggingPrompt = ({ codeSnippets, workflowData }) => {
  const messages = []

  const systemMessage = buildPrompt([{ content: systemMessages.EVENT_MODELING_REVERSE_ENGINEERING }])
  messages.push(createSystemMessage(systemMessage))

  const promptSections = []

  promptSections.push({
    title: 'Generated Workflow Data with IDs',
    content: `
        This is the complete workflow that was previously extracted from the code. It includes unique IDs for
        each command, query, and entity. You will use these IDs to tag the source code files.
      `,
    spacingAfter: 1
  })

  promptSections.push({
    contentType: 'codeblock',
    language: 'json',
    content: JSON.stringify(workflowData),
    flattenLines: false
  })

  if (codeSnippets && codeSnippets.length > 0) {
    promptSections.push({
      title: 'Source Code Files for Tagging',
      content: `
        The following are the original source code files. Your task is to analyze each one and
        determine which workflow components it is related to.
      `,
      spacingAfter: 1
    })
    codeSnippets.forEach((snippet, index) => {
      promptSections.push({
        title: `File path: ${snippet.path} | File name: ${snippet.name || ''}`,
        contentType: 'codeblock',
        language: getLanguageFromPath(snippet.path),
        content: snippet.content,
        spacingAfter: index === codeSnippets.length - 1 ? 2 : 1
      })
    })
  }

  promptSections.push({
    title: 'Task: Tag Source Code Files with Workflow Component IDs',
    content: `
      Your task is to associate each source code file with the workflow components it helps to implement.
      For each file in the "Source Code Files for Tagging" section, create a list of "tags". Each tag links the
      file to a specific command, query, or entity from the "Generated Workflow Data with IDs" section provided above.
      **IMPORTANT: In your final JSON response, the "path" property for each file MUST be the exact 'File path'
      value provided in the title of each code snippet. Do NOT include the 'File name' or the '(Part X/Y)'
      information in the 'path' property of your response.**
    `,
    spacingAfter: 0
  })
  promptSections.push({
    title: 'Tagging Rules',
    contentType: 'bullet',
    content: [
      'A tag **MUST** be a string in the format: `prefix-id`.',
      'The available prefixes are: `entity`, `command`, and `readmodel`.',
      'The `id` part of the tag **MUST** correspond to the `id` of the component in the provided workflow data.'
    ],
    spacingAfter: 0
  })
  promptSections.push({
    title: 'How to Decide on Tags',
    contentType: 'bullet',
    content: [
      `**entity-{id}**: Add this tag to a file if the file **defines or primarily represents** the entity
       with that 'id'. This is typically a model file (e.g., 'models/Customer.js').`,
      `**command-{id}**: Add this tag to a file if the file contains the **primary logic for executing** the
       command with that 'id'. This could be a controller action, a service method, or a command handler.
       A single file might handle multiple commands.`,
      `**readmodel-{id}**: Add this tag to a file if the file contains the **primary logic for fetching
       the data** for the query/read model with that 'id'. This could be a controller action for a GET request,
       a query service, or a repository method.`
    ]
  })

  promptSections.push({
    title: 'Important Considerations',
    contentType: 'bullet',
    content: [
      `A single file can (and often will) have **multiple tags**. For example, a controller file might handle
       a command and a read model, so it would have both 'command-{id}' and 'readmodel-{id}' tags.`,
      `If a file is not directly related to any specific entity, command, or read model (e.g., a generic
       utility or config file), it should not have any tags and should be omitted from the final output array.`,
      'Analyze the code content, not just the file path, to make your decisions.'
    ]
  })

  const additionalFormattingInstructions = `
    The output must be a single JSON object with one top-level key: "fileTags".
    The value of "fileTags" must be an array of objects.
    Each object in the array must have two keys:
    - "path": (String) The file path, **exactly as provided in the 'File path:' part of the input snippet's title**.
    - "tags": (Array of Strings) A list of all relevant "prefix-id" tags for that file.
    Only include files that have at least one tag in the final output.
  `

  promptSections.push({
    title: 'Formatting',
    flattenLines: false,
    content: getJsonOutputFormatting(
      'fileTags',
      JSON.stringify(exampleFileTagsOutput),
      additionalFormattingInstructions
    )
  })

  const userContent = buildPrompt(promptSections)
  messages.push(createUserMessage(userContent))

  return messages
}
